import { CommentIcon } from "@hightouch/htui";
import { splitBodyOnMentions } from "@hightouch/lib/llm/asset-comments/mention-tokens";
import { commentThreadParams } from "@hightouch/lib/llm/asset-comments/paths";

import type { InboxResponseKind } from "src/graphql";
import type {
  InboxGroup,
  InboxNotification,
} from "src/pages/projects/inbox/inbox-queries";
import { useEventContext } from "src/pages/projects/event-context";
import {
  describeEvent,
  type EventGlyph,
  eventGlyph,
  payloadString,
} from "src/pages/projects/utils";

type Event = InboxNotification["event"];

const NO_NAMES = new Map<string, string>();

const REASON_LABELS: Partial<Record<InboxGroup["reason"], string>> = {
  review: "Review requested",
  mention: "Mentioned you",
  reply: "Replied to you",
};

/** Why a group is about the viewer. Null when it's only something they follow. */
export function reasonLabel(reason: InboxGroup["reason"]): string | null {
  return REASON_LABELS[reason] ?? null;
}

/** How a request was settled: what the viewer did, or closed when its review stopped waiting on them. */
export type OutcomeKind = InboxResponseKind | "closed";

const OUTCOME_LABELS: Record<OutcomeKind, string> = {
  replied: "Replied",
  resolved: "Resolved",
  approved: "Approved",
  changes_requested: "Changes requested",
  closed: "Closed",
};

const OUTCOME_SENTENCES: Record<OutcomeKind, string> = {
  replied: "You replied",
  resolved: "Thread resolved",
  approved: "You approved",
  changes_requested: "You requested changes",
  closed: "No longer waiting on you",
};

/** How a request was settled, as a badge: "Replied". */
export function responseLabel(kind: OutcomeKind): string {
  return OUTCOME_LABELS[kind];
}

/** How a request was settled, as a sentence: "You replied". */
export function responseSentence(kind: OutcomeKind): string {
  return OUTCOME_SENTENCES[kind];
}

// What asked something of the viewer says so; an update reads as the event itself.
const REASON_VERBS: Partial<Record<InboxGroup["reason"], string>> = {
  review: "requested your review",
  mention: "mentioned you",
  reply: "replied to you",
};

// A run's line names this many of its updates and counts the rest.
const NAMED_UPDATES = 2;

/** What a row's actor did, written to follow their name. */
export function useBundleVerb(bundle: InboxBundle): string {
  const context = useEventContext();
  const [latest] = bundle.notifications;
  if (!latest) return "";
  if (bundle.notifications.length > 1) {
    return summarizeRun(
      bundle.notifications.map(({ event }) => describeEvent(event, context)),
    );
  }
  return REASON_VERBS[latest.reason] ?? describeEvent(latest.event, context);
}

/**
 * A run's updates, newest first, as one phrase in the order they happened: "created the task, assigned you, and 2 more".
 * Repeats of one update count as one, as "edited the file 3 times".
 */
export function summarizeRun(newestFirst: string[]): string {
  const updates = [...new Set([...newestFirst].reverse())];
  const [only] = updates;
  if (updates.length === 1 && only !== undefined) {
    return `${only} ${newestFirst.length} times`;
  }
  const named = updates.slice(0, NAMED_UPDATES);
  const rest = updates.length - named.length;
  if (rest === 0) return named.join(" and ");
  return `${named.join(", ")}, and ${rest} more`;
}

/** The body as written of a comment, or of the comment a review posted. Null for any other event. */
export function commentBody(event: Pick<Event, "payload">): string | null {
  return payloadString(event.payload, "body");
}

/** A comment's body on one line, with each mention as @name. Null for any other event. */
export function commentExcerpt(
  event: Pick<Event, "kind" | "payload">,
): string | null {
  const body = commentBody(event);
  if (body === null) return null;
  const text = splitBodyOnMentions(body, NO_NAMES)
    .map((segment) =>
      segment.kind === "text" ? segment.text : `@${segment.label}`,
    )
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  return text || null;
}

// Events further apart than this read as separate things, even from the same actor.
const BUNDLE_WINDOW_MS = 10 * 60_000;

/** One row of the list: a notification, or a run of similar ones from one actor at about the same time. */
export type InboxBundle = {
  /** The oldest notification's id, which stays put as newer ones join the run. */
  id: string;
  /** Newest first. */
  notifications: InboxNotification[];
};

function actorKey({ event }: InboxNotification): string {
  return `${event.actorType}:${event.actorUserId ?? ""}:${event.actorName}`;
}

/** The task, file or project a notification's event is about. */
function resourceKey({ event }: InboxNotification): string {
  if (event.task) return `task:${event.task.id}`;
  if (event.file) return `file:${event.file.id}`;
  return `project:${event.project?.id ?? ""}`;
}

/**
 * Whether two neighboring notifications go in one row: the same actor on the same task, file or project, for the same reason, minutes apart.
 * A comment, or a review that posted one, stands alone, since it's replied to on its own.
 */
function isSameRun(a: InboxNotification, b: InboxNotification): boolean {
  return (
    a.event.kind !== "commented" &&
    b.event.kind !== "commented" &&
    commentBody(a.event) === null &&
    commentBody(b.event) === null &&
    a.reason === b.reason &&
    actorKey(a) === actorKey(b) &&
    resourceKey(a) === resourceKey(b) &&
    Math.abs(Date.parse(a.createdAt) - Date.parse(b.createdAt)) <=
      BUNDLE_WINDOW_MS
  );
}

/** A group's notifications, newest first, with each run of similar ones in one bundle. */
export function bundleNotifications(
  notifications: InboxNotification[],
): InboxBundle[] {
  const runs: InboxNotification[][] = [];
  for (const notification of notifications) {
    const run = runs.at(-1);
    const previous = run?.at(-1);
    if (run && previous && isSameRun(previous, notification)) {
      run.push(notification);
    } else {
      runs.push([notification]);
    }
  }
  return runs.flatMap((run) => {
    const oldest = run.at(-1);
    return oldest ? [{ id: oldest.id, notifications: run }] : [];
  });
}

export function isBundleUnread(bundle: InboxBundle): boolean {
  return bundle.notifications.some((notification) => !notification.readAt);
}

/** The comment a lone comment or review notification is about, and its thread. Null for anything else. */
export function linkedComment(
  bundle: InboxBundle,
): { threadId: string; commentId: string } | null {
  const [notification] = bundle.notifications;
  if (bundle.notifications.length !== 1 || !notification) return null;
  const { event } = notification;
  const threadId = payloadString(event.payload, "threadId");
  const commentId = payloadString(event.payload, "commentId");
  return threadId && commentId ? { threadId, commentId } : null;
}

/** Where a comment opens on its resource, highlighted in its thread; anything else opens the resource. */
export function commentHref(bundle: InboxBundle, href: string): string {
  const comment = linkedComment(bundle);
  if (!comment) return href;
  const params = commentThreadParams({ commentId: comment.commentId });
  return `${href}${href.includes("?") ? "&" : "?"}${params}`;
}

const COMMENT_VERBS: Partial<Record<InboxNotification["reason"], string>> = {
  mention: "Mentioned you",
  reply: "Replied",
};

/** A notification's icon, verb and object. A comment's verb says why the viewer got it, and its object quotes it. */
export function useNotificationGlyph(
  notification: InboxNotification,
): EventGlyph | null {
  const context = useEventContext();
  const { event, reason } = notification;
  const glyph = eventGlyph(event, context);
  const excerpt = commentExcerpt(event);
  const quote = excerpt && `“${excerpt}”`;
  // A review quotes the comment it posted.
  if (glyph)
    return quote && !glyph.object ? { ...glyph, object: quote } : glyph;
  if (event.kind !== "commented") return null;
  return {
    icon: CommentIcon,
    verb: COMMENT_VERBS[reason] ?? "Commented",
    object: quote,
    href: null,
    suffix: null,
  };
}
